import { Controller } from "@hotwired/stimulus"

// Desktop-only list/grid toggle for the time-activities index.
//
// The selected mode is persisted **server-side** — toggling fires a
// fire-and-forget POST to /time_activities/view_mode which writes a
// year-long cookie via Set-Cookie. `ApplicationHelper#time_activities_view_mode`
// reads that cookie so the next render's <ul> classes match the
// user's choice on first paint, with no flash.
//
// On click we update the UI immediately (local optimism) and persist
// in parallel — the server is the source of truth for the next page
// load, but the user sees the toggle apply instantly.
//
// Targets:
//   list   — the <ul#time_activities>. The target attribute lives on
//            the partial itself, so Turbo Stream replacements (after
//            create / update / destroy) auto-reconnect the new <ul>
//            and listTargetConnected() repaints the current mode.
//   listBtn, gridBtn — the two toggle buttons.
const PERSIST_URL = "/time_activities/view_mode"
const LIST_CLASSES = ["space-y-3"]
const GRID_CLASSES = ["grid", "grid-cols-1", "lg:grid-cols-2", "xl:grid-cols-3", "gap-3"]
const ACTIVE_CLASSES   = ["bg-gray-900", "text-white", "dark:bg-gray-100", "dark:text-gray-900"]
const INACTIVE_CLASSES = ["bg-white", "text-gray-700", "dark:bg-gray-900", "dark:text-gray-200"]

export default class extends Controller {
  static targets = ["list", "listBtn", "gridBtn"]
  static values  = { mode: { type: String, default: "list" } }

  // modeValue is initialized from the data attribute the server set
  // (data-time-activities-view-mode-value). The server already
  // rendered the right <ul> classes and aria-pressed states, so
  // there's nothing to repaint on initial connect.

  showList() {
    this.setMode("list")
  }

  showGrid() {
    this.setMode("grid")
  }

  setMode(mode) {
    if (mode !== "list" && mode !== "grid") return
    if (mode === this.modeValue) return
    this.modeValue = mode  // triggers modeValueChanged()
    this.persist(mode)
  }

  // Stimulus calls this automatically when modeValue changes — so the
  // UI stays in sync with the value whether it was changed by a click,
  // server-set initial value, or anything else.
  modeValueChanged() {
    if (this.hasListTarget) this.applyListClasses(this.listTarget)
    this.paintButtons()
  }

  // Fires when Turbo Streams swap #time_activities back in after a
  // create/update. The server already rendered the right classes
  // (it reads the same cookie), but we re-apply defensively.
  listTargetConnected(ul) {
    this.applyListClasses(ul)
  }

  persist(mode) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(PERSIST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": token || "",
        "Accept": "application/json"
      },
      body: `mode=${encodeURIComponent(mode)}`,
      credentials: "same-origin"
    }).catch(() => { /* network blip — local state is still correct */ })
  }

  applyListClasses(ul) {
    ul.classList.remove(...LIST_CLASSES, ...GRID_CLASSES)
    ul.classList.add(...(this.modeValue === "grid" ? GRID_CLASSES : LIST_CLASSES))
  }

  paintButtons() {
    this.paintButton(this.hasListBtnTarget ? this.listBtnTarget : null, this.modeValue === "list")
    this.paintButton(this.hasGridBtnTarget ? this.gridBtnTarget : null, this.modeValue === "grid")
  }

  paintButton(btn, active) {
    if (!btn) return
    btn.setAttribute("aria-pressed", active ? "true" : "false")
    btn.classList.remove(...ACTIVE_CLASSES, ...INACTIVE_CLASSES)
    btn.classList.add(...(active ? ACTIVE_CLASSES : INACTIVE_CLASSES))
  }
}
